<template>
	<AppContentLayout v-if="flowData" class="flows-view">
		<div class="width-100 height-100 display-flex flex-align-center flex-column page-wrapper">
			<div class="box-container display-flex flex-column mb-9" v-if="showConfigurationBox">
				<div class="width-100 display-flex flex-row flex-align-center flex-justify-space-between">
					<div class="box-title">My experience</div>
					<div v-if="showEditSetup" @click="editConfiguration" class="display-flex flex-row flex-align-center edit-setup-container">
						<SIcon class="mr-1" name="settings" width="16" style="margin-top: 2px" />
						<div class="edit-setup-text">Edit Personal setup</div>
					</div>
				</div>
				<div class="title-description">Control how this feature works for you</div>
				<div v-if="showIsOnSwitch" class="display-flex flex-row flex-align-center flex-justify-space-between section-container mt-5 pr-6">
					<div class="section-title">My visibility</div>
					<FeatureVisibilityDropdown
						:isVisible="isFlowOn"
						:disabled="!canChangeState"
						:isLoading="loadingVisibilityChange"
						@set-visible="() => (isFlowOn = true)"
						@set-hidden="() => (isFlowOn = false)"
					/>
				</div>
			</div>
			<div class="box-container mt-2">
				<div class="box-title mb-6">Details</div>
				<div v-if="workflowDescription">
					<div class="section-title mt-9">Description</div>
					<div class="connection-text">{{ workflowDescription }}</div>
				</div>
				<div v-if="hasHowItWorks">
					<div class="section-title mt-9">How it works</div>
					<RequiresExtensionMessageBox v-if="showExtensionRequired && !isExtensionInstalled" class="mt-3 mb-4" />
					<div v-if="!isAddOn" class="display-flex flex-column">
						<div v-if="isToolMode">
							<div class="connection-text" v-if="isCommandBar">
								<div v-if="isExtensionInstalled">
									To use this tool, open the command bar <CommandSourtcutIndication /> and use this command:
								</div>
								<div v-else>To use this tool, open the command bar, and use this command:</div>
								<CommandBarDisplay class="mt-2" :command="flowData.tool_preferences.command_bar_title" />
							</div>
							<div v-else>
								<span v-if="isSlackTool" class="connection-text">
									To use this tool, open Slack and type <b>/ply {{ flowData.workflow_title }}</b>
								</span>
								<span v-else class="connection-text">
									To use this tool, go to: <LinkCopy class="link-copy-wrapper" :src="flowData.run_link" />
								</span>
								<template v-if="isPortal && isCustomDomainsEnabled">
									<Row class="flex-justify-space-between mt-3">
										<MaybeUpgradeRequiredIndicator :billingFeatureId="billingFeatureId">
											<template #default="{ indicator, isUpgradeRequired }">
												<Row class="flex-space-between--center">
													<div class="section-title mr-9">Or, customize url</div>
													<LinkCopy v-if="customURLExists" :src="customURL" class="link-copy-wrapper mr-2" />
													<Row>
														<a class="fs-14 purple-text" @click="(e) => editCustomURL(e, isUpgradeRequired, indicator)" href="#"
															>{{ customURLExists ? 'Edit' : 'Set Custom' }} URL</a
														>
														<div class="pl-2" v-if="billingFeatureId && isUpgradeRequired">
															<component :is="indicator" :billingFeatureId="billingFeatureId" iconOnly />
														</div>
													</Row>
												</Row>
											</template>
										</MaybeUpgradeRequiredIndicator>
									</Row>
								</template>
							</div>
						</div>

						<div v-else>
							<div class="connection-text">{{ howItWorks }}</div>
						</div>
					</div>
					<div v-else>
						<AddOnLocationExplanation :wfID="wfID" class="connection-text" />
					</div>
				</div>
				<div v-if="hasEditPermissions" class="mt-9">
					<div class="section-title">Version</div>
					<div class="title-description">Only admins can see changelog and unpublished drafts</div>
					<div class="connection-text mt-1">
						<div v-if="publishedVersion">
							Version {{ numOfVersions - 1 }} was published by {{ publishedVersionCreatorName }} {{ timeSinceLastPublish }}
						</div>
						<div v-if="draftVersion">
							Unpublished draft: Version {{ numOfVersions }} last edited by {{ draftVersionCreatorName }} {{ timeSinceLastDraftEdit }}
						</div>
					</div>
				</div>
			</div>
		</div>
	</AppContentLayout>
</template>

<script>
import moment from 'moment';
import { mapGetters, mapActions } from 'vuex';

import { FlowType } from '@ply-io/ply-front-shared-utilities';

import AppContentLayout from '../AppLayout/AppContentLayout';
import MaybeUpgradeRequiredIndicator from '@/components/Billing/MaybeUpgradeRequiredIndicator.vue';
import { FeatureTypes } from '@/components/Billing/billing-types';
import FeatureVisibilityDropdown from '@/components/FeatureVisibility/FeatureVisibilityDropdown.vue';
import CommandBarDisplay from '@/components/Modules/CommandBarDisplay';
import RequiresExtensionMessageBox from '@/components/Modules/RequiresExtensionMessageBox';
import CommandSourtcutIndication from '@/components/common/CommandSourtcutIndication';
import LinkCopy from '@/components/common/LinkCopy/LinkCopy';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { teamMembers } from '@/store/modules/account/getters';
import { getServiceBySlug } from '@/store/modules/apps/getters';
import { isConfigurationsOn, isCustomDomainsEnabled } from '@/store/modules/featureFlags/getters';
import { selectWorkflowId } from '@/store/modules/selectedWorkflow/actions';
import { toggleWorkflowIsOnSettings } from '@/store/modules/workflows/actions';
import { getWorkflow, hasWorkflowEditPermissions, hasWorkflowEditGeneralConfigurationPermissions } from '@/store/modules/workflows/getters';
import { mapGettersShorthand } from '@/store/utils/mappers';
import WorkflowService from '@/utils/WorkflowService';
import ExtensionCommunicationService from '@/utils/extension-communication-service';
import WorkflowConfigurationResponseService from '@/utils/workflow-configuration-response-service';
import WorkflowToolService from '@/utils/workflow-tool-service';
import AddOnLocationExplanation from '@/views/Flow/AddOnLocationExplanation.vue';

export default {
	name: 'FlowsDetails',
	props: {
		wfID: { type: Number },
	},
	components: {
		FeatureVisibilityDropdown,
		AppContentLayout,
		AddOnLocationExplanation,
		LinkCopy,
		CommandSourtcutIndication,
		RequiresExtensionMessageBox,
		CommandBarDisplay,
		MaybeUpgradeRequiredIndicator,
	},
	computed: {
		...mapGettersShorthand({ isCustomDomainsEnabled }),
		...mapGetters([
			teamMembers.fullPath,
			getWorkflow.fullPath,
			isConfigurationsOn.fullPath,
			hasWorkflowEditPermissions.fullPath,
			hasWorkflowEditGeneralConfigurationPermissions.fullPath,
			getServiceBySlug.fullPath,
		]),
		billingFeatureId() {
			return FeatureTypes.CustomerCustomDomains;
		},
		customURL() {
			return this.flowData.run_link;
		},
		customURLExists() {
			return !!this.flowData.tool_preferences.custom_domain;
		},
		isPortal() {
			return WorkflowService.isPublicAccess(this.flowData);
		},
		workflowDescription() {
			return this.flowData.onboarding_settings?.text;
		},
		canChangeState() {
			return WorkflowService.canHideWorkflow(this.flowData);
		},
		hasEditPermissions() {
			// has permission and is not installed from somewhere
			return this[hasWorkflowEditPermissions.fullPath](this.wfID);
		},
		userSetupId() {
			return this.flowData.current_version_user_setup;
		},
		publishedVersionCreatorName() {
			return this.getVersionCreatorName(this.publishedVersion);
		},
		publishedVersion() {
			return this.getVersionDetails(this.flowData.current_version);
		},
		timeSinceLastPublish() {
			return this.getTimeFromNow(this.publishedVersion?.updated_at);
		},
		draftVersion() {
			return this.flowData.draft_pending_changes_flag ? this.getVersionDetails(this.flowData.current_draft.id) : null;
		},
		draftVersionCreatorName() {
			return this.getVersionCreatorName(this.draftVersion);
		},
		timeSinceLastDraftEdit() {
			return this.getTimeFromNow(this.draftVersion?.updated_at);
		},
		generalSetupId() {
			return this.flowData.current_version_general_setup;
		},
		showConfigurationBox() {
			return this.showEditSetup || this.showIsOnSwitch;
		},
		showEditSetup() {
			return this[isConfigurationsOn.fullPath] && (this.generalSetupId || this.userSetupId);
		},
		showIsOnSwitch() {
			return WorkflowService.isToggleAddOnToggleable(this.flowData.flow_type, this.flowData.tool_preferences?.command_bar_enabled);
		},
		flowData() {
			return this[getWorkflow.fullPath](this.wfID);
		},
		numOfVersions() {
			return this.flowData.versions?.length;
		},
		isAddOn() {
			return this.flowData.flow_type === FlowType.Experience;
		},
		isNotification() {
			return this.flowData.is_notification;
		},
		isToolMode() {
			return this.flowData.flow_type === FlowType.Tool;
		},
		isCommandBar() {
			return WorkflowToolService.isCommandBarTool(this.flowData);
		},
		isSlackTool() {
			return WorkflowToolService.isSlackTool(this.flowData);
		},
		isOwner() {
			return this.flowData.is_owner;
		},
		flowCreator() {
			if (this.installedFromInstallable) {
				return 'installed from marketplace'; // For now dont show who created it for installables
			}
			if (this.isOwner) {
				return 'created by you';
			}
			const member = this[teamMembers.fullPath].find((member) => member.user_id === this.flowData.owner);
			if (member) {
				return 'created by ' + member.name;
			}
			return null;
		},
		installedFromInstallable() {
			return !!this.flowData.installed_from;
		},
		hasHowItWorks() {
			return this.flowData.flow_type === FlowType.Tool || !!this.howItWorks;
		},
		plyLink() {
			const base = this.flowData.ply_run_link;
			const url = new URL(base);
			return url.href;
		},
		howItWorks() {
			const flowType = this.flowData.flow_type;
			if (this.isNotification) {
				return `This feature sends notifications in the browser and Slack about events in ${this.contextService?.service_name}`;
			}
			switch (flowType) {
				case FlowType.Tool:
					if (WorkflowService.isPublicAccess(this.flowData)) {
						return 'Your customers can use this portal in ' + this.flowData.run_link;
					}
					break;
				case FlowType.Experience:
					return 'You get a notification in the channels selected above.';
				default:
					break;
			}
			return null;
		},
		isFlowOn: {
			get() {
				return this.flowData?.user_settings?.is_on;
			},
			async set(newV) {
				if (newV !== this.flowData?.user_settings?.is_on) {
					this.loadingVisibilityChange = true;
					try {
						await this[toggleWorkflowIsOnSettings.fullPath]({ id: this.wfID });
					} catch {
						this.$toast.error("Couldn't change feature visibility, please try again later");
					} finally {
						this.loadingVisibilityChange = false;
					}
				}
			},
		},
		hasEditGeneralConfigurationPermissions() {
			return this[hasWorkflowEditGeneralConfigurationPermissions.fullPath](this.flowData.id);
		},
		contextService() {
			return this[getServiceBySlug.fullPath](this.flowData.context_service_slug);
		},
		showExtensionRequired() {
			return this.isCommandBar || this.isAddOn || this.isNotification;
		},
	},
	methods: {
		...mapActions([selectWorkflowId.fullPath, toggleWorkflowIsOnSettings.fullPath]),
		getVersionDetails(versionId) {
			return this.flowData.versions?.find((ver) => ver.id === versionId);
		},
		editCustomURL(event, isUpgradeRequired, indicator) {
			if (isUpgradeRequired && indicator?.setup) {
				indicator.setup().openUpgrade(event, this.billingFeatureId);
			} else {
				AppDialogManagerService.openSetCustomDomainForWorkflowDialog({ workflow: this.flowData, plyLink: this.plyLink });
			}
		},
		getVersionCreatorName(version) {
			return version?.created_by?.first_name + ' ' + version?.created_by?.last_name;
		},
		getTimeFromNow(ts) {
			return moment(ts).fromNow();
		},
		editConfiguration() {
			this[selectWorkflowId.fullPath]({ id: this.wfID });
			return WorkflowConfigurationResponseService.editAndSaveConfiguration(
				{
					generalConfigurationId: this.hasEditGeneralConfigurationPermissions ? this.generalSetupId : null,
					userSpecificConfigurationId: this.userSetupId,
				},
				false
			);
		},
		async setIsExtensionInstalled() {
			if (this.flowData) {
				this.isExtensionInstalled = await ExtensionCommunicationService.isInstalled();
			}
		},
	},
	watch: {
		async flowData(newV, oldV) {
			if (newV && !oldV) {
				this.isFlowOn = newV.user_settings?.is_on;

				this.setIsExtensionInstalled(); // in case 'this.flowData' wasn't initilized when 'created()' ran
			}
		},
	},
	async created() {
		await this.setIsExtensionInstalled();
		this.isExtensionInstalledSubscription = ExtensionCommunicationService.subscribeToExtensionInstalled(this.setIsExtensionInstalled);
	},
	beforeDestroy() {
		ExtensionCommunicationService.unsubscribeToExtensionInstalled(this.isExtensionInstalledSubscription);
	},
	data() {
		return {
			isExtensionInstalled: true,
			loadingVisibilityChange: false,
			isExtensionInstalledSubscription: null,
		};
	},
};
</script>

<style scoped lang="scss">
.page-wrapper {
	overflow-y: auto;
	overflow-x: hidden;
}
.box-container {
	width: 100%;
	max-width: 560px;
	padding: 30px 66px 46px 36px;
	border-radius: 7px;
	border: 1px solid #e5e5e5;

	.box-title {
		font-size: 18px;
		color: #4d4c52;
		font-weight: 500;
	}
	.edit-setup-container {
		cursor: pointer;
		.edit-setup-text {
			font-size: 14px;
			font-weight: 400;
			color: #4d4c52;
		}
	}

	.title-description {
		font-weight: 400;
		font-size: 12px;
		color: #9e9e9e;
	}
}

.section-container {
	width: 480px;
}
.section-title {
	font-size: 14px;
	color: #4d4c52;
	font-weight: 500;
}
.section-link {
	font-size: 14px;
	cursor: pointer;
	color: #666666;
	font-weight: 400;
	text-decoration: underline;
}
.connection-text {
	font-weight: 400;
	font-size: 14px;
	color: #666666;
}
.link-copy-wrapper {
	max-width: 240px;
}
.fs-14 {
	font-size: 14px;
}
.mr-18 {
	margin-right: 72px;
}
.purple-text {
	color: #6162d8;
}
</style>
